import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, FileBarChart, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage, formatDate } from "@/lib/api";
import { Empty, Fold, InsightCard } from "@/components/AnalyticsCards";

export function useReportProgress() {
  return useQuery({ queryKey: ["report-progress"], queryFn: () => api.get("/analytics/progress").then((r) => r.data), staleTime: 15_000, refetchOnMount: "always" });
}

function Bar({ n, of }) {
  return (
    <div className="flex gap-1.5 mt-2.5" aria-hidden>
      {Array.from({ length: of }, (_, i) => <span key={i} className={`h-1.5 flex-1 rounded-full ${i < n ? "bg-[#E50914]" : "bg-[#27272A]"}`} />)}
    </div>
  );
}

const blockRange = (b, of) => `${(b - 1) * of + 1}–${b * of}`;

// Log page card: progress toward the next report, or a nudge to generate one.
export function ReportProgress() {
  const { data: p } = useReportProgress();
  if (!p) return null;
  const first = !p.report_no;
  let title, sub, bar = null;
  if (p.status === "generating") {
    title = "Building your Game Report…";
    sub = "It'll be on your Stats page in a moment.";
  } else if (p.can_generate) {
    title = `Game Report #${p.report_no + 1} is ready`;
    sub = `Tap to generate it from sessions ${blockRange(p.available_block, p.min_sessions)}.`;
  } else if (p.locked) {
    title = "A new Game Report is unlocked";
    sub = "Your first report was free. New reports every 3 sessions are part of Pro.";
  } else {
    const n = first ? p.organised_sessions : p.into_block;
    title = `${n}/${p.min_sessions} sessions until your ${first ? "first" : "next"} Game Report`;
    sub = first ? "It reads your own logs to show what's working and what to fix." : "Each report covers your latest 3 sessions.";
    bar = <Bar n={n} of={p.min_sessions} />;
  }
  return (
    <Link to="/analytics" className="card p-4 flex items-center gap-3 hover:border-[#3F3F46] transition-colors" data-testid="report-progress">
      <FileBarChart size={20} className="text-[#E50914] shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold" data-testid="report-progress-title">{title}</div>
        <div className="text-xs text-[#71717A] mt-0.5">{sub}</div>
        {bar}
      </div>
      <ArrowRight size={16} className="text-[#52525B] shrink-0" />
    </Link>
  );
}

// Stats page: header of the current report plus generate / locked / progress state.
export function ReportPanel({ r }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const generate = async () => {
    setBusy(true);
    try {
      await api.post("/analytics/report/generate");
      await qc.invalidateQueries({ queryKey: ["analytics"] });
      qc.invalidateQueries({ queryKey: ["report-progress"] });
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  const generating = r.status === "generating";
  return (
    <div className="space-y-2.5 pt-3" data-testid="report-panel">
      {r.insights && (
        <div className="flex items-end justify-between gap-3" data-testid="report-header">
          <div>
            <div className="eyebrow">Game Report</div>
            <div className="font-display text-3xl uppercase tracking-tight leading-none mt-1">#{r.report_no}</div>
          </div>
          {r.range && <div className="text-[11px] text-[#71717A] text-right">Sessions {r.range.first}–{r.range.last}<br />{formatDate(r.range.from)} – {formatDate(r.range.to)}</div>}
        </div>
      )}
      {generating ? (
        <div className="card p-4 flex items-center gap-2 text-sm text-[#A1A1AA]" data-testid="report-generating"><Loader2 size={15} className="animate-spin text-[#E50914]" /> Building Game Report #{r.report_no + 1} from your last 3 sessions…</div>
      ) : r.can_generate ? (
        <div className="card p-4" data-testid="report-ready">
          <div className="font-semibold text-[15px]">Game Report #{r.report_no + 1} is ready</div>
          <p className="text-[13px] text-[#A1A1AA] mt-1">Built only from sessions {blockRange(r.available_block, r.min_sessions)}: what worked, what went wrong and what to focus on next.</p>
          <button onClick={generate} disabled={busy} className="btn-primary w-full mt-3" data-testid="report-generate">
            {busy ? <Loader2 size={16} className="animate-spin" /> : "Generate Game Report"}
          </button>
        </div>
      ) : r.locked ? (
        <div className="card p-4 flex items-center gap-3" data-testid="report-locked">
          <Lock size={16} className="text-[#E50914] shrink-0" />
          <p className="text-sm text-[#A1A1AA] flex-1">Your last 3 sessions unlocked a new Game Report. New reports are part of Pro.</p>
          <Link to="/pro" className="text-sm font-semibold text-white underline decoration-[#E50914] underline-offset-4" data-testid="report-upgrade">Pro</Link>
        </div>
      ) : (
        <div className="card px-4 py-3" data-testid="report-next">
          <div className="text-[13px] text-[#A1A1AA]">{r.report_no ? r.into_block : r.organised_sessions}/{r.min_sessions} sessions until your {r.report_no ? "next" : "first"} Game Report</div>
          <Bar n={r.report_no ? r.into_block : r.organised_sessions} of={r.min_sessions} />
        </div>
      )}
      {r.status === "error" && !generating && <p className="text-xs text-[#FF6B6B]" data-testid="report-error">The last report couldn't be built. Please try again.</p>}
    </div>
  );
}

export function FreeReport({ r }) {
  const ins = r.insights;
  if (!ins) return null;
  return (
    <div className="space-y-2.5" data-testid="free-report">
      <Fold title="What to focus on next" summary={ins.focus.length === 1 ? "1 priority" : `${ins.focus.length} priorities`} testid="free-report-focus">
        {ins.focus.length ? ins.focus.map((f, i) => (
          <InsightCard key={i} item={f} tone="focus" testid={`free-focus-${i}`}>
            {f.drill && <p className="text-[13px] text-[#A1A1AA] mt-2"><span className="font-semibold text-white">Drill:</span> {f.drill}</p>}
          </InsightCard>
        )) : <Empty>No clear priority yet. Describe what went wrong and why in your next logs.</Empty>}
      </Fold>
      <Fold title="What's working" summary={ins.working.length ? `${ins.working.length} found` : "None yet"} testid="free-report-working">
        {ins.working.length ? ins.working.map((w, i) => <InsightCard key={i} item={w} tone="good" badge={`Reported ${w.sessions}×`} testid={`free-working-${i}`} />) : <Empty>No successes reported in these sessions.</Empty>}
      </Fold>
      <Fold title="Recurring problems" summary={ins.problems.length ? `${ins.problems.length} found` : "None found"} testid="free-report-problems">
        {ins.problems.length ? ins.problems.map((p, i) => <InsightCard key={i} item={p} tone="bad" badge={`${p.sessions} sessions`} testid={`free-problem-${i}`} />) : <Empty>Nothing came up in 2 or more of these sessions.</Empty>}
      </Fold>
    </div>
  );
}
